import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type AccountType =
  | "Checking"
  | "Savings"
  | "CreditCard"
  | "LineOfCredit"
  | "Brokerage"
  | "Retirement"
  | "Other";

export type AccountPurpose =
  | "Operating"
  | "TaxSavings"
  | "Reserves"
  | "OwnerCapital"
  | "Retirement"
  | "Other";

export interface FinancialAccount {
  id: string;
  ownerUserId: string;
  name: string;
  accountType: AccountType;
  purpose: AccountPurpose;
  institution?: string | null;
  last4?: string | null;
  currency: string;
  openingBalance: number;
  openingBalanceDate?: string | null;
  isArchived: boolean;
}

export type TransactionDirection =
  | "Income"
  | "Expense"
  | "Transfer"
  | "OwnerDraw"
  | "OwnerContribution"
  | "Tax"
  | "Retirement";

export type TransactionScope = "Business" | "Personal" | "Mixed";

export interface Transaction {
  id: string;
  ownerUserId: string;
  financialAccountId: string;
  date: string;
  description: string;
  memo?: string | null;
  counterParty?: string | null;
  direction: TransactionDirection;
  amount: number;
  currency: string;
  chartOfAccountId?: string | null;
  sourceInvoiceId?: string | null;
  importSource?: string | null;
  isReconciled: boolean;
  /** Phase C: Business / Personal / Mixed-use scope tag. */
  scope: TransactionScope;
  /** Phase C: 0..1 deductible-portion when scope = Mixed. Null otherwise. */
  businessUsePct: number | null;
}

export type InvoiceStatus =
  | "Draft"
  | "Sent"
  | "Paid"
  | "PartiallyPaid"
  | "Overdue"
  | "Voided";

export interface InvoiceLineItem {
  id: string;
  invoiceId: string;
  sortOrder: number;
  description: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  lineItemType: "Hours" | "Overtime" | "Bonus" | "ExpenseReimbursement" | "Flat";
}

export interface Invoice {
  id: string;
  ownerUserId: string;
  number: string;
  contactId?: string | null;
  issueDate: string;
  dueDate: string;
  status: InvoiceStatus;
  currency: string;
  subtotal: number;
  tax: number;
  total: number;
  amountPaid: number;
  notes?: string | null;
  paidOn?: string | null;
  lineItems: InvoiceLineItem[];
}

const ACCOUNTS_KEY = ["money", "accounts"];
const TXNS_KEY = ["money", "transactions"];
const INVOICES_KEY = ["money", "invoices"];

export function useAccounts() {
  return useQuery({
    queryKey: ACCOUNTS_KEY,
    queryFn: async (): Promise<FinancialAccount[]> => {
      const r = await api().get<FinancialAccount[]>("/api/accounts");
      return r.data;
    },
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<FinancialAccount>) => {
      const r = await api().post<FinancialAccount>("/api/accounts", input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useTransactions(params: { accountId?: string; from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: [...TXNS_KEY, params],
    queryFn: async (): Promise<Transaction[]> => {
      const r = await api().get<Transaction[]>("/api/transactions", { params });
      return r.data;
    },
  });
}

export function useCreateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Transaction>) => {
      const r = await api().post<Transaction>("/api/transactions", input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TXNS_KEY });
      qc.invalidateQueries({ queryKey: ACCOUNTS_KEY });
    },
  });
}

export function useInvoices() {
  return useQuery({
    queryKey: INVOICES_KEY,
    queryFn: async (): Promise<Invoice[]> => {
      const r = await api().get<Invoice[]>("/api/invoices");
      return r.data;
    },
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Invoice>) => {
      const r = await api().post<Invoice>("/api/invoices", input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: INVOICES_KEY }),
  });
}

// Phase C: auto-classification suggestions and applier.
export type SuggestionConfidence = "Low" | "Medium" | "High";

export interface ClassificationSuggestion {
  suggestedScope: TransactionScope;
  suggestedChartOfAccountId: string;
  suggestedChartOfAccountCode: string;
  suggestedChartOfAccountName: string;
  suggestedBusinessUsePct: number | null;
  confidence: SuggestionConfidence;
  reason: string;
  matchedTerm: string;
}

export interface TransactionWithSuggestion {
  transactionId: string;
  suggestion: ClassificationSuggestion;
}

export function useTransactionSuggestions() {
  return useQuery({
    queryKey: ["money", "suggestions"],
    queryFn: async (): Promise<TransactionWithSuggestion[]> => {
      const r = await api().get<TransactionWithSuggestion[]>("/api/transactions/suggestions");
      return r.data;
    },
    staleTime: 30_000,
  });
}

export function useApplyClassification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      chartOfAccountId: string | null;
      scope: TransactionScope;
      businessUsePct: number | null;
    }) => {
      await api().patch(`/api/transactions/${input.id}/classification`, {
        chartOfAccountId: input.chartOfAccountId,
        scope: input.scope,
        businessUsePct: input.businessUsePct,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TXNS_KEY });
      qc.invalidateQueries({ queryKey: ["money", "suggestions"] });
      qc.invalidateQueries({ queryKey: ["tax", "projection"] });
      qc.invalidateQueries({ queryKey: ["tax", "schedule-a"] });
    },
  });
}

export function useMarkInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      invoiceId: string;
      depositAccountId: string;
      paidOn: string;
      amount: number;
      payerName?: string;
      source?: string;
    }) => {
      const r = await api().post(`/api/invoices/${input.invoiceId}/mark-paid`, {
        depositAccountId: input.depositAccountId,
        paidOn: input.paidOn,
        amount: input.amount,
        payerName: input.payerName,
        source: input.source ?? "manual",
      });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INVOICES_KEY });
      qc.invalidateQueries({ queryKey: TXNS_KEY });
    },
  });
}
